// ABOUTME: Marketing splash page shown to unauthenticated visitors.
// ABOUTME: Editorial 4-section layout: hero, product screenshot, value props, footer.
// ABOUTME: Uses hardcoded Tailwind classes (not CSS variables) so it's immune to user theme changes.

import React from "react";
import { Link } from "react-router-dom";
import { APP_NAME, APP_AUTHOR, APP_TAGLINE } from "../constants/branding";

interface SplashPageProps {
  onSignInClick?: () => void;
  onGetStartedClick?: () => void;
  onTryAsGuestClick?: () => void;
}

const SplashPage: React.FC<SplashPageProps> = ({
  onSignInClick,
  onGetStartedClick,
  onTryAsGuestClick,
}) => (
  <div className="min-h-screen bg-stone-50">
    {/* Section 1: Hero */}
    <div className="px-6 md:px-8 pt-12 pb-10">
      {/* Top bar */}
      <div className="flex justify-between items-center mb-16 max-w-4xl mx-auto">
        <div className="flex items-baseline gap-2">
          <span className="font-heading text-lg font-semibold tracking-tight text-stone-900">
            {APP_NAME}
          </span>
          <span className="text-xs uppercase tracking-widest text-stone-400">{APP_TAGLINE}</span>
        </div>
        <button
          onClick={onSignInClick}
          className="px-3.5 py-1.5 border border-stone-200 text-stone-600 text-xs font-medium rounded hover:border-stone-300 transition-colors"
        >
          Sign in
        </button>
      </div>

      {/* Headline */}
      <div className="max-w-4xl mx-auto">
        <h1 className="font-heading text-[2rem] md:text-[2.625rem] font-medium tracking-[-0.035em] leading-[1.1] text-stone-900 mb-4">
          A start page that stays
          <br />
          out of your way.
        </h1>
        <p className="text-[0.9375rem] text-stone-500 leading-relaxed max-w-[360px] mb-8">
          Your links, your tools, your morning routine — in one quiet place. Just what you need.
        </p>
        {/* Say what each button does: the primary creates an account and
            emails a sign-in link; guest mode works with no account at all. */}
        <div className="flex flex-wrap gap-2 items-center mb-3">
          <button
            onClick={onGetStartedClick}
            className="px-5 py-2.5 bg-sky-700 text-white text-[0.8125rem] font-medium rounded hover:opacity-90 transition-opacity"
          >
            Create an account
          </button>
          <button
            onClick={onTryAsGuestClick}
            className="px-5 py-2.5 border border-stone-200 text-stone-700 text-[0.8125rem] font-medium rounded hover:border-stone-300 hover:bg-stone-100 transition-colors"
          >
            Try as guest &rarr;
          </button>
          <button
            onClick={onSignInClick}
            className="px-5 py-2.5 text-stone-500 text-[0.8125rem] font-medium rounded hover:text-stone-600 hover:bg-stone-100 transition-colors"
          >
            Sign in
          </button>
        </div>
        <p className="text-xs text-stone-500 leading-relaxed max-w-[420px] mb-12">
          {APP_NAME} is invite-only while it&rsquo;s in beta. Have a code? Create an account and
          we&rsquo;ll email a sign-in link right away &mdash; no password. No code yet? Try it as a
          guest: your links stay in this browser until you sign in.
        </p>

        {/* Accent line */}
        <div
          className="h-px"
          style={{
            background: "linear-gradient(90deg, #0369a1 0%, #0369a1 20%, #e7e5e4 20%)",
          }}
        />
      </div>
    </div>

    {/* Section 2: Product screenshot */}
    <div className="px-6 md:px-8 pb-12">
      <div className="max-w-4xl mx-auto">
        <div className="bg-stone-200 rounded-lg border border-stone-200 overflow-hidden">
          {/* Browser chrome */}
          <div className="px-3 py-2 bg-stone-100 border-b border-stone-200 flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-[#fca5a5]" />
            <div className="w-2 h-2 rounded-full bg-[#fde68a]" />
            <div className="w-2 h-2 rounded-full bg-[#86efac]" />
            <span className="ml-3 text-xs text-stone-400 tracking-wide">start.brizzly.com</span>
          </div>
          {/* Dashboard mockup */}
          <div className="p-5 bg-stone-50">
            {/* Header bar */}
            <div className="flex justify-between items-center mb-5 pb-3 border-b border-stone-200">
              <span className="text-sm font-semibold text-stone-900 tracking-tight">S</span>
              <div className="w-44 h-7 bg-stone-100 border border-stone-200 rounded" />
              <div className="w-6 h-6 bg-sky-200 rounded-full" />
            </div>
            {/* Card grid */}
            <div className="grid grid-cols-3 gap-2.5">
              {[
                { color: "#fca5a5", w1: "60%", w2: "40%" },
                { color: "#93c5fd", w1: "70%", w2: "35%" },
                { color: "#86efac", w1: "55%", w2: "45%" },
                { color: "#fde68a", w1: "50%", w2: "30%" },
              ].map((card, i) => (
                <div key={i} className="bg-white border border-stone-200 rounded-md p-3">
                  <div className="w-4 h-4 rounded-sm mb-2" style={{ background: card.color }} />
                  <div className="h-2 bg-stone-300 rounded-sm mb-1" style={{ width: card.w1 }} />
                  <div className="h-1.5 bg-stone-200 rounded-sm" style={{ width: card.w2 }} />
                </div>
              ))}
              {/* Scratchpad widget */}
              <div className="bg-white border border-stone-200 rounded-md p-3 row-span-2">
                <div className="text-xs tracking-widest uppercase text-sky-700 mb-2">
                  Scratchpad
                </div>
                <div className="w-[90%] h-1.5 bg-stone-200 rounded-sm mb-1.5" />
                <div className="w-[70%] h-1.5 bg-stone-200 rounded-sm mb-1.5" />
                <div className="w-[80%] h-1.5 bg-stone-200 rounded-sm" />
              </div>
              <div className="bg-white border border-stone-200 rounded-md p-3">
                <div className="w-4 h-4 rounded-sm mb-2" style={{ background: "#c4b5fd" }} />
                <div className="w-[65%] h-2 bg-stone-300 rounded-sm mb-1" />
                <div className="w-[40%] h-1.5 bg-stone-200 rounded-sm" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Section 3: Value props */}
    <div className="px-6 md:px-8 py-12 border-t border-stone-200">
      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
        <div>
          <div className="text-xs uppercase tracking-widest text-sky-700 mb-2">Your links</div>
          <h3 className="font-heading text-lg font-medium tracking-tight text-stone-900 leading-snug mb-2">
            Pin what matters.
            <br />
            Forget the rest.
          </h3>
          <p className="text-[0.8125rem] text-stone-500 leading-relaxed">
            Drag your daily links into columns. No algorithms deciding what you see first.
          </p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-sky-700 mb-2">Your tools</div>
          <h3 className="font-heading text-lg font-medium tracking-tight text-stone-900 leading-snug mb-2">
            Widgets that earn
            <br />
            their space.
          </h3>
          <p className="text-[0.8125rem] text-stone-500 leading-relaxed">
            Notes, news, games — install only what you use. Remove what you don't.
          </p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-sky-700 mb-2">Your pace</div>
          <h3 className="font-heading text-lg font-medium tracking-tight text-stone-900 leading-snug mb-2">
            Your rhythm.
            <br />
            Your rules.
          </h3>
          <p className="text-[0.8125rem] text-stone-500 leading-relaxed">
            {APP_NAME} is a place, not a distraction. Open it, do your thing, move on.
          </p>
        </div>
      </div>
    </div>

    {/* Section 4: Footer */}
    <div className="px-6 md:px-8 py-6 border-t border-stone-200">
      <div className="max-w-4xl mx-auto flex justify-between items-center">
        <span className="text-xs text-stone-400">{APP_AUTHOR} &middot; 2026</span>
        <div className="flex gap-4">
          <Link to="/faq" className="text-xs text-stone-400 hover:text-sky-700 transition-colors">
            FAQ
          </Link>
          <Link to="/privacy" className="text-xs text-sky-700 hover:underline">
            Privacy
          </Link>
          <Link to="/terms" className="text-xs text-stone-400 hover:text-sky-700 transition-colors">
            Terms
          </Link>
        </div>
      </div>
    </div>
  </div>
);

export default SplashPage;
